import { router, usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { Loader2, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { agentChatHref } from '@/frontend/layouts/worker-first/agentAccess'
import { persistSelectedWorker, selectedWorkerStorageContextFromProps } from '@/frontend/layouts/worker-first/useSelectedWorker'
import { createSessionOverHttp } from '@/frontend/pages/Dashboard/AgentChats/api'
import { randomUuid } from '@/frontend/lib/randomUuid'
import type { SharedProps } from '@/frontend/types/inertia'
import type { Copy, ExecutionWorker } from '@/frontend/pages/Dashboard/Workflows/types'
import useWorkflowNavigation from '@/frontend/pages/Dashboard/Workflows/useWorkflowNavigation'

export default function WorkflowExecuteButton({ workflow, organizationId, tenant, workers, copy, primary = false, disabled = false }: {
  workflow: string; organizationId: string | number; tenant: string | null; workers: ExecutionWorker[]; copy: Copy; primary?: boolean; disabled?: boolean
}) {
  const { props: shared } = usePage<SharedProps>()
  const { projectId } = useWorkflowNavigation()
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(() => workers.find((worker) => worker.id === shared.lastDigitalWorkerId && worker.chat_ready)?.id ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pending = useRef(false)
  const controller = useRef<AbortController | null>(null)
  const context = JSON.stringify([shared.auth.user?.id, organizationId, projectId, tenant, workflow])
  const activeContext = useRef(context)
  const target = workers.find((worker) => worker.id === selectedId && worker.chat_ready)

  useEffect(() => {
    activeContext.current = context
    setOpen(false); setError(null); setSubmitting(false); pending.current = false
    return () => { controller.current?.abort(); activeContext.current = '' }
  }, [context])

  async function submit() {
    if (!target || pending.current || disabled) return
    pending.current = true; setSubmitting(true); setError(null)
    controller.current = new AbortController()
    // Preserve only the creation attempt after an ambiguous response. Acceptance
    // clears this draft; subsequent intentional executions get a new submission ID.
    const draftKey = `praxis-workflow-kickoff:${context}:${target.id}`
    try {
      const previous = sessionStorage.getItem(draftKey)
      const submissionId = previous && /^[0-9a-f-]{36}$/i.test(previous) ? previous : randomUuid()
      sessionStorage.setItem(draftKey, submissionId)
      const session = await createSessionOverHttp({
        workerId: target.id, source: 'praxis_workflow', workflowType: workflow,
        fdeProjectId: projectId,
        options: { clientSubmissionId: submissionId, signal: controller.current.signal, responseTimeoutMs: 20000 },
      })
      if (controller.current.signal.aborted || activeContext.current !== context) return
      sessionStorage.removeItem(draftKey)
      persistSelectedWorker(target.id, selectedWorkerStorageContextFromProps(shared))
      router.visit(agentChatHref(target.id, { sessionId: session.id }))
    } catch (err) {
      if (controller.current?.signal.aborted || activeContext.current !== context) return
      setError(err instanceof Error && 'status' in err ? err.message : copy.kickoff.error)
      setSubmitting(false); pending.current = false
    }
  }

  return <Dialog open={open} onOpenChange={(next) => { if (!pending.current) setOpen(next) }}>
    <DialogTrigger asChild><Button type="button" disabled={disabled} variant={primary ? 'default' : 'outline'}
      aria-label={copy.kickoff.accessible_label.replace('%{workflow}', workflow)}
      className={primary ? 'h-9 shrink-0 gap-[7px] rounded-lg px-3.5 text-[13px] shadow-none' : 'h-8 w-[163px] shrink-0 gap-[7px] rounded-lg border-input bg-card px-3 text-[13px] text-brand shadow-none'}>
      <Play className="size-3.5" aria-hidden />{copy.kickoff.execute}
    </Button></DialogTrigger>
    <DialogContent className="max-w-[calc(100vw-32px)] sm:max-w-lg" onEscapeKeyDown={(event) => { if (submitting) event.preventDefault() }}>
      <DialogHeader><DialogTitle>{copy.kickoff.choose_agent}</DialogTitle>
        <DialogDescription>{copy.kickoff.description.replace('%{workflow}', workflow)}</DialogDescription>
      </DialogHeader>
      <label className="space-y-2 text-sm font-medium"><span className="block">{copy.kickoff.agent}</span>
        <select aria-label={copy.kickoff.agent} value={selectedId} disabled={submitting} onChange={(event) => setSelectedId(event.target.value)}
          className="h-10 w-full rounded-lg border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <option value="">{copy.kickoff.choose_placeholder}</option>
          {workers.map((worker) => <option key={worker.id} value={worker.id} disabled={!worker.chat_ready}>{worker.name}{worker.chat_ready ? '' : ` — ${copy.kickoff.unavailable_agent}`}</option>)}
        </select>
      </label>
      {workers.length === 0 && <p className="text-sm text-muted-foreground">{copy.kickoff.no_agents}</p>}
      {submitting && <p role="status" className="text-sm text-muted-foreground">{copy.kickoff.opening}</p>}
      {error && <div role="alert" className="space-y-1 text-sm text-destructive"><p>{error}</p><p className="text-xs text-muted-foreground">{copy.kickoff.recovery_hint}</p></div>}
      <DialogFooter><Button type="button" variant="outline" disabled={submitting} onClick={() => setOpen(false)}>{copy.kickoff.cancel}</Button>
        <Button type="button" disabled={!target || submitting || disabled} onClick={submit} aria-busy={submitting}>
          {submitting ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <Play className="size-3.5" aria-hidden />}{copy.kickoff.execute}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
}
